import { gql } from "@apollo/client";
import GraphQlService from "./graphql";

class surveyGraphql extends GraphQlService {
  async createSurveyTemplates(data) {
    return this.client
      .mutate({
        mutation: gql`
          mutation {
            createSurveyTemplates(createSurveyTemplatesInput: {
              name: "${data.name}"
              content: """${data.content}"""
              accessType: "${data.accessType}"
              surveyType: "${data.surveyType}"
              public: ${data.public}
              active: ${data.active}
              autoSave: ${data.autoSave}
              accountId: ${data.accountId ? `"${data.accountId}"` : null}
            }) {
            id 
            } 
          }
          `,
      })
      .then((result) => result?.data?.createSurveyTemplates);
  }

  async updateSurveyTemplates(data) {
    return this.client
      .mutate({
        mutation: gql`
          mutation {
            updateSurveyTemplates(updateSurveyTemplatesInput:{
              name: "${data.name}"
              content: """${data.content}"""
              accessType: "${data.accessType}"
              surveyType: "${data.surveyType}"
              public: ${data.public}
              active: ${data.active}
              autoSave: ${data.autoSave}
              id: "${data.id}"
            }) {
              id
            }
          }
          `,
      })
      .then((result) => result?.data?.updateSurveyTemplates);
  }

  async updateStatusSurveyTemplate(data) {
    return this.client
      .mutate({
        mutation: gql`
          mutation {
            updateStatusSurveyTemplate(updateStatusSurveyTemplateInput:{
              id: "${data.id}"
              active: ${data.active}
            }) {
              id
            }
          }
          `,
      })
      .then((result) => result?.data?.updateStatusSurveyTemplate);
  }

  async updateAutoSaveSurveyTemplate(id, autoSave) {
    return this.client
      .mutate({
        mutation: gql`
          mutation {
            updateAutoSaveSurveyTemplate(updateAutoSaveSurveyTemplateInput:{
              id: "${id}"
              autoSave: ${autoSave}
            }) {
              id
              autoSave
            }
          }
          `,
      })
      .then((result) => result?.data?.updateAutoSaveSurveyTemplate);
  }

  async getAllSurveyTemplates() {
    return this.client
      .query({
        query: gql`
          query {
            getAllSurveyTemplates {
              id
              name
              content
              accessType
              surveyType
              public
              active
            }
          }
        `,
      })
      .then((result) => result?.data?.getAllSurveyTemplates);
  }

  // `pagination` optional — omitted entirely, the backend returns the full
  // (active-only) set in one call (same convention as
  // UsersGraphqlInstance.getAllUsers). Admin-only; doesn't touch the
  // COUNSELLOR/PRINCIPAL visibility filtering getAllSurveyTemplates above
  // has.
  async getSurveyTemplatesPage(pagination = {}) {
    const { limit, offset, search, sortField, sortOrder } = pagination;
    return this.client
      .query({
        query: gql`
          query GetSurveyTemplatesPage(
            $limit: Float
            $offset: Float
            $search: String
            $sortField: String
            $sortOrder: String
          ) {
            getSurveyTemplatesPage(
              limit: $limit
              offset: $offset
              search: $search
              sortField: $sortField
              sortOrder: $sortOrder
            ) {
              total
              items {
                id
                name
                content
                accessType
                surveyType
                public
                active
              }
            }
          }
        `,
        variables: {
          limit: limit ?? null,
          offset: offset ?? null,
          search: search ?? null,
          sortField: sortField ?? null,
          sortOrder: sortOrder ?? null,
        },
        fetchPolicy: "no-cache",
      })
      .then((result) => result?.data?.getSurveyTemplatesPage);
  }

  async removeSurveyTemplates(id) {
    return this.client
      .mutate({
        mutation: gql`
          mutation{
            removeSurveyTemplates(
              id: "${id}"
            )
          }
          `,
      })
      .then((result) => result?.data?.removeSurveyTemplates);
  }

  async getSurveyTemplates(id) {
    return this.client
      .query({
        query: gql`
        query{
            getSurveyTemplates(
              id: "${id}"
            ){
              id
              name
              content
              accessType
              surveyType
              public
              active
              autoSave
            }
          }
        `,
      })
      .then((result) => result?.data?.getSurveyTemplates);
  }

  async getAllSurveyConfig() {
    return this.client
      .query({
        query: gql`
          query {
            getAllSurveyConfig {
              id
              active
              account {
                name
              }
              surveyTemplate {
                name
              }
              isAnonymous
              userAttributes
              createdAt
              surveyTimer {
                runOnce
                startDate
                endDate
              }
            }
          }
        `,
      })
      .then((result) => result?.data?.getAllSurveyConfig);
  }

  // `pagination` optional — omitted entirely, the backend returns the full
  // set in one call. No `filters` arg — this table has no column filters
  // in the UI today.
  async getSurveyConfigPage(pagination = {}) {
    const { limit, offset, search, sortField, sortOrder } = pagination;
    return this.client
      .query({
        query: gql`
          query GetSurveyConfigPage(
            $limit: Float
            $offset: Float
            $search: String
            $sortField: String
            $sortOrder: String
          ) {
            getSurveyConfigPage(
              limit: $limit
              offset: $offset
              search: $search
              sortField: $sortField
              sortOrder: $sortOrder
            ) {
              total
              items {
                id
                active
                account {
                  name
                }
                surveyTemplate {
                  name
                }
                isAnonymous
                userAttributes
                createdAt
                surveyTimer {
                  runOnce
                  startDate
                  endDate
                }
              }
            }
          }
        `,
        variables: {
          limit: limit ?? null,
          offset: offset ?? null,
          search: search ?? null,
          sortField: sortField ?? null,
          sortOrder: sortOrder ?? null,
        },
        fetchPolicy: "no-cache",
      })
      .then((result) => result?.data?.getSurveyConfigPage);
  }

  async setSurveyConfig(data) {
    return this.client
      .mutate({
        mutation: gql`
        mutation {
          setSurveyConfig(setSurveyConfigInput:{
            surveyTemplateId: "${data.surveyTemplateId}",
            accountId: """${JSON.stringify(data.accountId)}""",
            isAnonymous: ${data.isAnonymous},
            timer: ${
              Array.isArray(data.timer)
                ? `"""${JSON.stringify(data.timer)}"""`
                : `"${data.timer}"`
            } ,
            startDate: "${data.startDate}",
            endDate: "${data.endDate}",
            runOnce: "${data.runOnce}",
            timeLimit: ${data.timeLimit ? `"${data.timeLimit}"` : null},
            userAttributes:${
              data.userAttributes != null
                ? data.userAttributes === "custom"
                  ? `"${data.userAttributes}"`
                  : `"""${JSON.stringify(data.userAttributes)}"""`
                : `"""${null}"""`
            },
            isAllowForGuest: ${data.isAllowForGuest ?? false},
            customUsersIds: ${
              data.customUsersIds?.length > 0
                ? `"""${JSON.stringify(data.customUsersIds)}"""`
                : null
            }
          })
      } 
        `,
      })
      .then((result) => result?.data?.setSurveyConfig);
  }

  async getSurveyConfig(id) {
    return this.client
      .query({
        query: gql`
        query{
          getSurveyConfig(
              id: "${id}"
            ){
              accountId
              surveyTemplateId
              isAnonymous
              active
              surveyTemplate{
                content
                name
                surveyType
              }
              surveyTimer{
                id
                timer
                runOnce
                startDate
                endDate
                timeLimit
              }
            }
          }
        `,
      })
      .then((result) => result?.data?.getSurveyConfig);
  }

  async updateSurveyConfig(data) {
    return this.client
      .mutate({
        mutation: gql`
        mutation {
          updateSurveyConfig(updateSurveyConfigInput:{
            surveyTemplateId: "${data.surveyTemplateId}",
            accountId: "${data.accountId}",
            active: ${data.active}
            isAnonymous: ${data.isAnonymous},
            timer: ${data.timer ? `"${data.timer}"` : null},
            startDate: "${data.startDate}",
            endDate: "${data.endDate}",
            runOnce: "${data.runOnce}",
            timeLimit: ${data.timeLimit ? `"${data.timeLimit}"` : null},
            surveyTimerId: "${data.surveyTimerId}",
            surveyConfigId: "${data.surveyConfigId}",
            userAttributes:${
              data.userAttributes != null
                ? data.userAttributes == "custom"
                  ? `"${data.userAttributes}"`
                  : `"""${JSON.stringify(data.userAttributes)}"""`
                : `"""${null}"""`
            }
          })
      } 
        `,
      })
      .then((result) => result?.data?.updateSurveyConfig)
      .catch((error) => {
        console.error("Error updating survey config:", error);
      });
  }
  async updateStatusSurveyConfig(data) {
    return this.client
      .mutate({
        mutation: gql`
        mutation {
          updateStatusSurveyConfig(updateStatusSurveyConfigInput:{
            id: "${data.id}",
            active: ${data.active}
          }){
            id
          }
      } 
        `,
      })
      .then((result) => result?.data?.updateStatusSurveyConfig);
  }

  async removeSurveyConfig(id) {
    return this.client
      .mutate({
        mutation: gql`
          mutation{
            removeSurveyConfig(
              id: "${id}"
            )
          }
          `,
      })
      .then((result) => result?.data?.removeSurveyConfig);
  }
  async getSurveyAndCheckIns() {
    return this.client
      .query({
        query: gql`
          query {
            getSurveyAndCheckIns {
              id
              surveyTemplate {
                surveyType
                name
                content
              }
            }
          }
        `,
      })
      .then((result) => result?.data?.getSurveyAndCheckIns);
  }
  async createSurveyResults(data) {
    return this.client
      .mutate({
        mutation: gql`
          mutation{
            createSurveyResults(createSurveyResultsInput:{
              content: """${JSON.stringify(data.content)}""",
              surveyConfigId:"${data.surveyConfigId}",
              guestUserClicked: ${data.isGuestUserClicked ?? false}
            }){
              id
            }
          }
          `,
      })
      .then((result) => result?.data?.createSurveyResults);
  }

  async skipSurvey(id, guestUserClicked = false) {
    return this.client
      .mutate({
        mutation: gql`
        mutation{
          skipSurvey(createSurveyResultsInput:{
            surveyConfigId:"${id}",
            guestUserClicked: ${guestUserClicked}
          }){
            id
          }
        }
          `,
      })
      .then((result) => result?.data?.skipSurvey);
  }

  //statistics
  async getAllSurveyQuestionsStatistics(
    dtStart,
    dtEnd,
    surveyConfigIds,
    selectedUserId,
    deepFilterData,
    accountIds = null,
    surveyTemplateId = null,
    selected = null,
    resultId = null
  ) {
    return this.client
      .query({
        query: gql`
          query {
            getAllSurveyQuestionsStatistics(getAllSurveyQuestionsStatisticsInput:{
              dtStart:"${dtStart}"
              dtEnd:"${dtEnd}"
              configId:${
                Array.isArray(surveyConfigIds)
                  ? `"""${JSON.stringify(surveyConfigIds)}"""`
                  : `"${surveyConfigIds}"`
              }
              userId : ${
                selectedUserId == null
                  ? null
                  : `"""${JSON.stringify(selectedUserId)}"""`
              }
              deepFilterData: ${
                deepFilterData.length > 0
                  ? `"""${JSON.stringify(deepFilterData)}"""`
                  : null
              }
              accountId:${
                accountIds != null
                  ? `"""${JSON.stringify(accountIds)}"""`
                  : null
              }
              surveyTemplateId: "${surveyTemplateId}"
              selected: ${
                selected != null ? `"""${JSON.stringify(selected)}"""` : null
              }
              resultId: ${
                resultId != null ? `"""${JSON.stringify(resultId)}"""` : null
              }
            }){
              templateName
              runOnce
              allUser
              organizationName
              attributes
              amountOfAnswer
              organizations{
                accountId
                name
              }
              allOriginalAttributes
              startDate
              endDate
              questions{
                questionName
                questionType
                unansweredCount
                unansweredUserId
                unansweredResultIds
                unansweredPercentage
                otherAnswers{
                    resultId
                    answer
                  }
                options{
                  name
                  value
                  count
                  studentIds
                  resultIds
                  surveyQuestionName
                }
              }
              allCustomStudentCount
              amountOfCustomStudentAnswer
              resultIds
            }
          }
          `,
      })
      .then((result) => result?.data?.getAllSurveyQuestionsStatistics);
  }

  async getCurrentOrganizationAllSurveyConfig() {
    return this.client
      .query({
        query: gql`
          query {
            getCurrentOrganizationAllSurveyConfig {
              id
              allUser
              amountOfAnswer
              isMultiple
              surveyConfigIds
              userAttributes
              active
              isAllowForGuest
              link
              account {
                id
                name
              }
              isAnonymous
              surveyTemplate {
                id
                surveyType
                name
              }
              surveyTimer {
                id
                timeLimit
                timer
                startDate
                endDate
                runOnce
              }
              organizations {
                accountId
                name
              }
            }
          }
        `,
      })
      .then((result) => result?.data?.getCurrentOrganizationAllSurveyConfig);
  }

  async getRecursionChartsDataSurveyQuestions(
    dtStart,
    dtEnd,
    configId,
    selectedUserId,
    deepFilterData
  ) {
    return this.client
      .query({
        query: gql`
          query {
            getRecursionChartsDataSurveyQuestions(getRecursionChartsDataSurveyQuestionsInput:{
              dtStart:"${dtStart}"
              dtEnd:"${dtEnd}"
              configId:"${configId}"
              userId : ${
                selectedUserId == null
                  ? null
                  : `"""${JSON.stringify(selectedUserId)}"""`
              }
              deepFilterData: ${
                deepFilterData.length > 0
                  ? `"""${JSON.stringify(deepFilterData)}"""`
                  : null
              }
            }){
              startDate
              endDate
              label
              type
              data{
                questionName
                questionType
                options{
                  name
                  value
                  count
                }
              }
            }
          }
          `,
      })
      .then((result) => result?.data?.getRecursionChartsDataSurveyQuestions);
  }

  async getSurveyTemplateStatistics(dtStart, dtEnd, templateId, accountIds) {
    return this.client
      .query({
        query: gql`
          query {
            getSurveyTemplateStatistics(getSurveyTemplateStatisticsInput:{
              dtStart:"${dtStart}"
              dtEnd:"${dtEnd}"
              templateId:"${templateId}"
              accountIds:${
                accountIds != null
                  ? `"""${JSON.stringify(accountIds)}"""`
                  : null
              }
            }){
              templateName
              allUser
              
              amountOfAnswer
              organizations{
                accountId
                name
              }
              questions{
                questionName
                questionType
                unansweredCount
                unansweredPercentage
                otherAnswers{
                    resultId
                    answer
                  }
                options{
                  name
                  value
                  count
                }
              }
            }
          }
          `,
      })
      .then((result) => result?.data?.getSurveyTemplateStatistics);
  }

  async endSurveyConfig(surveyConfigId) {
    return this.client
      .mutate({
        mutation: gql`
          mutation{
            endSurveyConfig(
              surveyConfigId:"${surveyConfigId}"
            )
          }
          `,
      })
      .then((result) => result?.data?.endSurveyConfig);
  }

  async getAllSurveyResultStudentsByConfigId(
    dtStart,
    dtEnd,
    configId,
    userId,
    resultIds = [],
    deepFilterData = []
  ) {
    return this.client
      .query({
        query: gql`
          query {
            getAllSurveyResultStudentsByConfigId(getAllSurveyResultStudentsByConfigIdInput:{
              dtStart: "${dtStart}"
              dtEnd: "${dtEnd}"
              id: ${
                Array.isArray(configId)
                  ? `"""${JSON.stringify(configId)}"""`
                  : `"${configId}"`
              }
              userId: """${JSON.stringify(userId)}"""
              resultId: ${
                resultIds.length > 0
                  ? `"""${JSON.stringify(resultIds)}"""`
                  : null
              }
              deepFilterData: ${
                deepFilterData.length > 0
                  ? `"""${JSON.stringify(deepFilterData)}"""`
                  : null
              }
            }){
              users {
                      id
                      accountPeople {
                        id
                        isOnline
                        profilePictureName
                        name
                        accountPersonAttributes {
                          name
                          value
                        }
                      }
                    }
                    results {
                      id
                      userId
                      createdAt
                    }
            }
          }
          `,
      })
      .then((result) => result?.data?.getAllSurveyResultStudentsByConfigId);
  }

  async getAllGuestSurveyResultsBySurveyConfigId(
    dtStart,
    dtEnd,
    surveyConfigId,
    resultIds = [],
    deepFilterData = []
  ) {
    return this.client
      .query({
        query: gql`
          query {
            getAllGuestSurveyResultsBySurveyConfigId(
              getAllGuestSurveyResultsBySurveyConfigIdInput:{
                dtStart: "${dtStart}"
                dtEnd: "${dtEnd}"
                surveyConfigId: "${surveyConfigId}"
                resultId: ${
                  resultIds.length > 0
                    ? `"""${JSON.stringify(resultIds)}"""`
                    : null
                }
                deepFilterData: ${
                  deepFilterData.length > 0
                    ? `"""${JSON.stringify(deepFilterData)}"""`
                    : null
                }
              }
              ){
               id
               surveyConfigId
               template
               createdBy
               status
               content
            }
          }
          `,
      })
      .then((result) => result?.data?.getAllGuestSurveyResultsBySurveyConfigId);
  }

  async getAllSurveyQuestionsStatisticsForGuest(
    configId,
    dtStart,
    dtEnd,
    deepFilterData,
    resultIds
  ) {
    return this.client
      .query({
        query: gql`
          query {
            getAllSurveyQuestionsStatisticsForGuest(getGuestSurveyStatisticsInput:{
              configId: ${
                Array.isArray(configId)
                  ? `"""${JSON.stringify(configId)}"""`
                  : `"${configId}"`
              }
              dtStart:"${dtStart}"
              dtEnd:"${dtEnd}"
              deepFilterData: ${
                deepFilterData.length > 0
                  ? `"""${JSON.stringify(deepFilterData)}"""`
                  : null
              }
              resultIds: ${
                resultIds.length > 0
                  ? `"""${JSON.stringify(resultIds)}"""`
                  : null
              }
            }){
              allUser
              questions{
                questionName
                questionType
                unansweredCount
                unansweredUserId
                unansweredResultIds
                unansweredPercentage
                otherAnswers{
                    resultId
                    answer
                  }
                options{
                  name
                  value
                  count
                  studentIds
                  resultIds
                  surveyQuestionName
                }
              }
            }
          }
          `,
      })
      .then((result) => result?.data?.getAllSurveyQuestionsStatisticsForGuest);
  }

  async downloadSurveyStatistics(
    dtStart,
    dtEnd,
    surveyConfigIds,
    selectedUserId,
    deepFilterData,
    accountIds = null,
    surveyTemplateId = null,
    selected = null,
    resultId = null,
    isAllowForGuest = false
  ) {
    return this.client
      .query({
        query: gql`
          query {
            downloadSurveyStatistics(getAllSurveyQuestionsStatisticsInput:{
              dtStart:"${dtStart}"
              dtEnd:"${dtEnd}"
              configId:${
                Array.isArray(surveyConfigIds)
                  ? `"""${JSON.stringify(surveyConfigIds)}"""`
                  : `"${surveyConfigIds}"`
              }
              userId : ${
                selectedUserId == null
                  ? null
                  : `"""${JSON.stringify(selectedUserId)}"""`
              }
              deepFilterData: ${
                deepFilterData.length > 0
                  ? `"""${JSON.stringify(deepFilterData)}"""`
                  : null
              }
              accountId:${
                accountIds != null
                  ? `"""${JSON.stringify(accountIds)}"""`
                  : null
              }
              surveyTemplateId: "${surveyTemplateId}"
              selected: ${
                selected != null ? `"""${JSON.stringify(selected)}"""` : null
              }
              resultId: ${
                resultId != null ? `"""${JSON.stringify(resultId)}"""` : null
              }
              isAllowForGuest: ${isAllowForGuest}
            })
          }
        `,
      })
      .then((result) => result?.data?.downloadSurveyStatistics);
  }
}
const surveyGraphqlService = new surveyGraphql();
export default surveyGraphqlService;
